"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const ConnectForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-primary/5 border border-primary/10 rounded-lg animate-in fade-in zoom-in duration-300">
        <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center text-primary">
          <Send className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-serif font-semibold text-foreground">Message Sent</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background/50 border-white/10 focus:border-primary/50 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background/50 border-white/10 focus:border-primary/50 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="bg-background/50 border-white/10 focus:border-primary/50 transition-colors" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70">
                Optional – for quicker follow-up
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Organization</FormLabel>
              <FormControl>
                <Input placeholder="Your company or group" {...field} className="bg-background/50 border-white/10 focus:border-primary/50 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you or what would you like to discuss?" className="min-h-[120px] bg-background/50 border-white/10 focus:border-primary/50 transition-colors resize-none" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/70">
              Please provide details so we can respond effectively
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px] bg-primary text-primary-foreground hover:bg-primary/90 font-medium tracking-wide">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
};
export default function ConnectWithUs() {
  return <section id="connect-with-us" className="dark bg-background text-foreground w-full relative overflow-hidden">
      <div className="grid lg:grid-cols-2 min-h-[800px]">
        {}
        <div className="relative w-full h-[400px] lg:h-full bg-muted overflow-hidden group">
          <div className="absolute inset-0 bg-black/20 z-10 pointer-events-none" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/map_grayscale_hq.png" alt="Map location of Colorado Hispanic Chamber of Commerce" className="w-full h-full object-cover grayscale contrast-125 transition-transform duration-1000 group-hover:scale-105" />
          
          {}
          <div className="absolute bottom-8 left-8 z-20 bg-card/90 backdrop-blur-md border border-white/10 p-6 rounded-sm shadow-2xl max-w-xs hidden md:block">
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="h-5 w-5 text-primary shrink-0 mt-1" />
              <div>
                <h4 className="font-serif font-semibold text-lg text-foreground">Headquarters</h4>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                  Colorado, USA
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-primary font-medium uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Open Now
            </div>
          </div>
        </div>

        {}
        <div className="relative flex flex-col justify-center p-8 md:p-12 lg:p-16 xl:p-20 bg-[#0B1120] border-l border-white/5">
          {}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

          <div className="relative z-10 max-w-xl mx-auto lg:mx-0 w-full">
            <div className="mb-10">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4 tracking-tight">
                Connect With Us
              </h2>
              <p className="text-lg text-muted-foreground">
                Reach out to our team for inquiries, media requests, or support. We are here to empower your business.
              </p>
            </div>

            {}
            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <div className="space-y-4">
                <div className="flex items-start gap-3 group">
                  <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Phone</p>
                    <Link to="tel:+13035551234" className="text-base font-semibold text-white hover:text-primary transition-colors block">
                      +1 (303) 555-1234
                    </Link>
                    <span className="text-xs text-muted-foreground/60">Mon-Fri, 9am-5pm</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3 group">
                  <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Email</p>
                    <Link to="mailto:info@cohcc.org" className="text-base font-semibold text-white hover:text-primary transition-colors block">
                      info@cohcc.org
                    </Link>
                    <span className="text-xs text-muted-foreground/60">General Inquiries</span>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="bg-card/30 backdrop-blur-sm border border-white/5 rounded-xl p-6 md:p-8 shadow-xl">
              <h3 className="text-xl font-serif font-semibold text-white mb-6 flex items-center gap-2">
                Send a Message
                <span className="h-px flex-1 bg-white/10 ml-4"></span>
              </h3>
              
              <FormProvider formId="connect_form_contact" sectionName="connect-with-us" defaultValues={{
              name: "",
              email: "",
              phone: "",
              organization: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you for reaching out. A member of our team will be in touch shortly." errorMessage="We couldn't send your message. Please try again later." className="w-full">
                <ConnectForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}